import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  InputAdornment,
  Divider,
  FormControlLabel,
  Switch,
  Autocomplete,
  MenuItem,
  Select,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  List as ListIcon,
} from "@mui/icons-material";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';

// Medical Application Form Section Names
const MEDICAL_SECTIONS = [
  { id: 'laboratory-details', name: 'Laboratory Details' },
  { id: 'organization', name: 'Organization' },
  { id: 'proficiency-testing', name: 'Proficiency Testing' },
  { id: 'discipline', name: 'Discipline' },
  { id: 'scope', name: 'Scope' },
  { id: 'fees', name: 'Fees' },
  { id: 'lab-staff', name: 'Lab Staff' },
  { id: 'sub-user', name: 'Sub User' },
  { id: 'equipment', name: 'Equipment' },
  { id: 'reference-material', name: 'Reference Material' },
  { id: 'infrastructure', name: 'Infrastructure' },
  { id: 'documentation', name: 'Documentation' },
  { id: 'enclosure', name: 'Enclosure' },
  { id: 'declaration', name: 'Declaration' },
  { id: 'phantoms', name: 'Phantoms' },
  { id: 'radioisotopes', name: 'Radioisotopes' },
  { id: 'non-analytical-instruments', name: 'Non-Analytical Instruments' },
  { id: 'final-submit', name: 'Final Submit' },
];

const InstructionMaster = () => {
  const [instructions, setInstructions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingInstruction, setEditingInstruction] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [instructionToDelete, setInstructionToDelete] = useState(null);

  const [filters, setFilters] = useState({
    sectionName: '',
    orderNo: '',
  });

  const [formData, setFormData] = useState({
    sectionName: '',
    orderNo: '',
    instruction: '',
    isActive: 1,
    labTypeId: '',
  });

  // Lab Type options
  const labTypes = [
    { id: 1, name: 'Testing' },
    { id: 2, name: 'Calibration' },
    { id: 3, name: 'Medical' }
  ];

  // Fetch all instructions
  const fetchInstructions = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.sectionName) params.append('sectionName', filters.sectionName);
      if (filters.orderNo) params.append('orderNo', filters.orderNo);

      const response = await fetch(`${API_BASE_URL}/instructions?${params.toString()}`);
      const data = await response.json();

      if (data.success) {
        setInstructions(data.data.instructions || []);
      } else {
        setError(data.message || 'Failed to fetch instructions');
        showSnackbar(data.message || 'Failed to fetch instructions', 'error');
      }
    } catch (error) {
      console.error('Error fetching instructions:', error);
      setError('Failed to fetch instructions');
      showSnackbar('Failed to fetch instructions', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstructions();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    fetchInstructions();
  };

  const resetFilters = () => {
    setFilters({
      sectionName: '',
      orderNo: '',
    });
    setTimeout(() => fetchInstructions(), 100);
  };

  const openModal = () => {
    setFormData({
      sectionName: '',
      orderNo: '',
      instruction: '',
      isActive: 1,
    });
    setEditingInstruction(null);
    setShowModal(true);
  };

  const openEditModal = (instruction) => {
    setFormData({
      sectionName: instruction.sectionName,
      orderNo: instruction.orderNo,
      instruction: instruction.instruction,
      isActive: instruction.isActive,
      labTypeId: instruction.labTypeId || '',
    });
    setEditingInstruction(instruction);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingInstruction(null);
    setFormData({
      sectionName: '',
      orderNo: '',
      instruction: '',
      isActive: 1,
      labTypeId: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingInstruction
        ? `${API_BASE_URL}/instructions/${editingInstruction.id}`
        : `${API_BASE_URL}/instructions`;
      
      const method = editingInstruction ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        showSnackbar(data.message || `Instruction ${editingInstruction ? 'updated' : 'created'} successfully`, 'success');
        closeModal();
        fetchInstructions();
      } else {
        showSnackbar(data.message || 'Operation failed', 'error');
      }
    } catch (error) {
      console.error('Error saving instruction:', error);
      showSnackbar('Failed to save instruction', 'error');
    }
  };

  const openDeleteConfirm = (instruction) => {
    setInstructionToDelete(instruction);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setInstructionToDelete(null);
  };

  const deleteInstruction = async () => {
    if (!instructionToDelete) return;

    try {
      const response = await fetch(`${API_BASE_URL}/instructions/${instructionToDelete.id}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        showSnackbar('Instruction deleted successfully', 'success');
        fetchInstructions();
      } else {
        showSnackbar(data.message || 'Failed to delete instruction', 'error');
      }
    } catch (error) {
      console.error('Error deleting instruction:', error);
      showSnackbar('Failed to delete instruction', 'error');
    } finally {
      closeDeleteConfirm();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ListIcon sx={{ fontSize: 32, color: "primary.main" }} />
          <Typography variant="h4" fontWeight="bold" color="primary">
            Instruction Master
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openModal}
          sx={{
            textTransform: "none",
            px: 3,
            py: 1,
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          Add Instruction
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <FilterListIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography variant="h6" fontWeight="600">
              Filters
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={MEDICAL_SECTIONS}
                getOptionLabel={(option) => option.name}
                value={
                  MEDICAL_SECTIONS.find(
                    (s) => s.name === filters.sectionName,
                  ) || null
                }
                onChange={(event, newValue) => {
                  setFilters((prev) => ({
                    ...prev,
                    sectionName: newValue ? newValue.name : "",
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Section Name"
                    placeholder="Type to search..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                freeSolo
                onInputChange={(event, newInputValue) => {
                  if (event && event.type === "change") {
                    setFilters((prev) => ({
                      ...prev,
                      sectionName: newInputValue,
                    }));
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Order No"
                name="orderNo"
                type="number"
                value={filters.orderNo}
                onChange={handleFilterChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  startIcon={<SearchIcon />}
                  sx={{ textTransform: "none" }}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  onClick={resetFilters}
                  startIcon={<RefreshIcon />}
                  sx={{ textTransform: "none" }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Instructions Table */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && instructions.length === 0 && (
          <Box sx={{ textAlign: "center", p: 6 }}>
            <ListIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No instructions found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Create your first instruction to get started
            </Typography>
          </Box>
        )}

        {!loading && instructions.length > 0 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "primary.main" }}>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>
                    Sr No.
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>
                    Lab Type
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>
                    Section Name
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>
                    Order No
                  </TableCell>
                  <TableCell
                    sx={{ color: "white", fontWeight: 600, minWidth: 300 }}
                  >
                    Instruction
                  </TableCell>
                  <TableCell
                    sx={{ color: "white", fontWeight: 600 }}
                    align="center"
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{ color: "white", fontWeight: 600 }}
                    align="center"
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {instructions.map((instruction, index) => {
                  const labType = labTypes.find(lt => lt.id === instruction.labTypeId);
                  return (
                    <TableRow
                      key={instruction.id}
                      sx={{
                        "&:hover": { backgroundColor: "action.hover" },
                        backgroundColor:
                        index % 2 === 0 ? "background.default" : "white",
                    }}
                  >
                    <TableCell>
                      <Chip
                        label={index + 1}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">
                        { labType?.name || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">
                        {instruction.sectionName || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={instruction.orderNo}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={instruction.instruction} arrow>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 400,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {instruction.instruction}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={instruction.isActive ? "Active" : "Inactive"}
                        size="small"
                        color={instruction.isActive ? "success" : "default"}
                        variant={instruction.isActive ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          justifyContent: "center",
                        }}
                      >
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEditModal(instruction)}
                            sx={{
                              "&:hover": {
                                backgroundColor: "primary.light",
                                color: "white",
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => openDeleteConfirm(instruction)}
                            sx={{
                              "&:hover": {
                                backgroundColor: "error.light",
                                color: "white",
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                )})}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onClose={closeModal} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle sx={{ backgroundColor: "primary.main", color: "white" }}>
            {editingInstruction ? "Edit Instruction" : "Add New Instruction"}
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} sx={{ mt: 2 }}>
                <Select
                  fullWidth
                  name="labTypeId"
                  value={formData.labTypeId}
                  onChange={handleInputChange}
                  displayEmpty
                  required
                  variant="outlined"
                >
                  <MenuItem value="" disabled>
                    <em>--Select Lab Type--</em>
                  </MenuItem>
                  {labTypes.map((lab) => (
                    <MenuItem key={lab.id} value={lab.id}>
                      {lab.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  fullWidth
                  options={MEDICAL_SECTIONS}
                  getOptionLabel={(option) => option.name}
                  value={
                    MEDICAL_SECTIONS.find(
                      (s) => s.name === formData.sectionName,
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      sectionName: newValue ? newValue.name : "",
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Section Name"
                      required
                      margin="normal"
                      placeholder="Type to search sections..."
                    />
                  )}
                  freeSolo
                  onInputChange={(event, newInputValue) => {
                    if (event && event.type === "change") {
                      setFormData((prev) => ({
                        ...prev,
                        sectionName: newInputValue,
                      }));
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Order No"
                  name="orderNo"
                  type="number"
                  value={formData.orderNo}
                  onChange={handleInputChange}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Instruction"
                  name="instruction"
                  value={formData.instruction}
                  onChange={handleInputChange}
                  required
                  multiline
                  rows={4}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive === 1}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          isActive: e.target.checked ? 1 : 0,
                        }))
                      }
                      color="primary"
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={closeModal}
              variant="outlined"
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ textTransform: "none" }}
            >
              {editingInstruction ? "Update" : "Save"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={closeDeleteConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: "error.main" }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this instruction?
          </Typography>
          {instructionToDelete && (
            <Box
              sx={{ mt: 2, p: 2, backgroundColor: "grey.100", borderRadius: 1 }}
            >
              <Typography variant="body2" fontWeight="bold">
                Section: {instructionToDelete.sectionName}
              </Typography>
              <Typography variant="body2">
                Order: {instructionToDelete.orderNo}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={closeDeleteConfirm}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={deleteInstruction}
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InstructionMaster;
